import { useCallback, useState } from 'react';
import { useFormikContext } from 'formik';
import FormikPersist from '@/utils/formik-persist';
import DraftRestoredBanner from './draft-restored-banner';

type Props = {
  name: string | null;
  excludeFields?: string[];
  hydrate?: (values: Record<string, unknown>) => Record<string, unknown>;
};

const DraftAutosave = ({ name, excludeFields, hydrate }: Props) => {
  const formik = useFormikContext();
  const [restoredAt, setRestoredAt] = useState<number | null | undefined>(undefined);
  const [imageDropped, setImageDropped] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(false);

  const handleRestore = useCallback((savedAt: number | null, droppedFields: string[]) => {
    setRestoredAt(savedAt);
    setImageDropped(droppedFields.includes('image'));
  }, []);

  const handleDiscard = useCallback(() => {
    const confirmed = !formik.dirty
      || window.confirm('Discard all draft data and start with an empty form?');
    if (!confirmed) return;
    if (name) {
      window.localStorage.removeItem(name);
    }
    formik.resetForm();
    setRestoredAt(undefined);
  }, [formik, name]);

  const handleDismiss = useCallback(() => {
    setBannerDismissed(true);
  }, []);

  if (!name) return null;

  const showBanner = restoredAt !== undefined && !bannerDismissed;

  return (
    <>
      <FormikPersist
        name={name}
        excludeFields={excludeFields}
        hydrate={hydrate}
        onRestore={handleRestore}
      />
      {showBanner && (
        <DraftRestoredBanner
          savedAt={restoredAt ?? null}
          showImageNote={imageDropped}
          onDiscard={handleDiscard}
          onDismiss={handleDismiss}
        />
      )}
    </>
  );
};

export default DraftAutosave;
